/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/sketch/identity 层；
 * 2. 负责草图“运行时 uuid ↔ 持久业务 ID”的唯一归一入口；
 * 3. 纯映射工具，不持有场景引用、不产生副作用、不依赖任何其它模块。
 *
 * 文件职责：
 * 1. 由持有草图的一方（Viewer / SketchGroupManager / 投影输入）构建身份索引；
 * 2. 把命令、特征绑定等持久空间中的草图 ID 归一为场景运行时句柄；
 * 3. 让设计树投影、退回时间线、特征编辑共用同一套判定，杜绝各消费点自行 `===` 比对。
 *
 * 背景约束：
 * 特征绑定改为原生存储草图持久 ID 后，凡是拿绑定里的草图 ID 直接和场景 uuid 比对的调用点，
 * 都会把同一张草图判成两张（吸收关系丢失、编辑目标解析失败）。所有此类比对必须经本模块。
 */

/**
 * 构建草图身份归一索引。
 *
 * 设计说明：
 * 1. 索引同时登记运行时 uuid 与持久 ID 两把键，值统一为运行时 uuid；
 * 2. 运行时 uuid 自映射，保证只有运行时身份的旧数据也能命中；
 * 3. 缺失持久 ID 的条目（旧会话数据）只登记运行时键，不产生空键污染。
 *
 * @param entries 当前场景中的草图身份条目。
 * @returns 由任意草图 ID 指向运行时 uuid 的归一索引。
 */
export function createSketchIdentityIndex(
    entries: readonly PuppyCAD.SketchIdentityEntry[],
): Map<string, string> {
    const identityIndex = new Map<string, string>();
    entries.forEach((entry) => {
        const runtimeId = entry.runtimeId;
        if (typeof runtimeId !== 'string' || runtimeId.length === 0) {
            return;
        }
        identityIndex.set(runtimeId, runtimeId);

        const persistentId = entry.persistentId;
        if (typeof persistentId !== 'string' || persistentId.length === 0) {
            return;
        }
        identityIndex.set(persistentId, runtimeId);
    });
    return identityIndex;
}

/**
 * 把任意来源的草图 ID 归一为场景运行时句柄。
 *
 * 设计说明：
 * 1. 未登记的 ID 原样返回，保证旧数据与测试替身不会被静默清空；
 * 2. 空值统一收敛为 `null`，调用点不必各自判空；
 * 3. 该函数是“持久空间 → 运行时空间”的唯一合法通道。
 *
 * @param identityIndex 草图身份归一索引。
 * @param sketchId 待归一的草图 ID，可能是运行时 uuid 或持久 ID。
 * @returns 场景运行时 uuid；无法归一时返回原值；输入为空时返回 null。
 */
export function normalizeSketchIdToRuntime(
    identityIndex: ReadonlyMap<string, string>,
    sketchId: string | null | undefined,
): string | null {
    if (typeof sketchId !== 'string' || sketchId.length === 0) {
        return null;
    }
    return identityIndex.get(sketchId) ?? sketchId;
}
